<!--
  SizeApprovalOverride.vue -- approve one SIZE on behalf of a client.

  Jake, 2026-10-21: "For unapproved decorations in the decoration library,
  there should be a way to override approval on behalf of the client."

  One mechanism, not two
  ======================
  The override IS the existing builder bypass
  (POST /api/approval_records/<id>/bypass -> approval_service.bypass_stale),
  the same act the Approvals workspace offers. It moves the size's
  ApprovalRecord to approved -- so the launch gate, the store rollup and
  every other reader see an approved size -- and audits it as a `bypass`
  action carrying who, the picked reason and the free-text note.

  What it renders, per card
  =========================
  * Each client this size still awaits (`card.awaiting_clients`), and --
    only for a caller holding distributor.decoration_library.override --
    an "Approve on behalf of <client>" action.
  * Each approval the distributor gave on a client's behalf
    (`card.approval_overrides`): who, and why. These are deliberately NOT
    in the card's "Approved by" chip -- the client did not approve them.

  Only `awaiting_approval` is offered. A size the client asked to change is
  a different act (the state machine refuses changes_requested -> approved).

  A size NEVER SENT to a client (Bug Intake #332; Jake, 2026-10-24) has no
  record to bypass -- the case on a surface where approvals are switched off
  for testing, so nothing is ever sent. For it the card offers "Approve on
  behalf of a client": pick one of the artwork's distributor's clients (the
  artwork's own client preselected when it has exactly one), a reason and a
  note, and POST /api/decorations/<id>/approve-on-behalf writes the size's
  approved record, audited as the same `bypass` act. The picker leaves out
  clients this size already has (approved, or awaiting -- those have their
  own action above). One panel is open per card at a time.

  The confirm step requires a reason AND a free-text note: the note is the
  "why" a later reader of the audit trail needs. The note is INTERNAL (Jake,
  2026-10-21): it lives only on the audit row (ApprovalAction.notes); the
  client-visible approval thread gets the reason alone. The form says so. After a successful
  override the parent re-reads the cards from the server rather than
  patching this card locally (principle 4 -- one source).

  Retract (Bug Intake #732)
  =========================
  An approval made on a client's behalf can be retracted, with a REQUIRED
  reason, by whoever made it or an Owner (or a Super Admin) -- the same rule
  and pattern as retracting a team member's "approved before upload"
  statement (TeamAttestationList.vue). The API decides who may, per override
  (`can_retract`); the action is shown only to an override holder.
  POST /api/decorations/<id>/approve-on-behalf/retract moves the size's
  record back to awaiting the client (it had been sent) or not sent, so it
  stops counting as approved, including for opening stores. The reason is
  internal (on the audit row only). After a retract the row says so and the
  parent is told (`retracted`) so it re-reads the cards.
-->
<template>
    <div
        v-if="awaiting.length || overrides.length || showUnsentAction || retractedNotice"
        class="sao"
        :data-testid="`dla-card-approvals-${card.id}`"
    >
        <div
            v-for="o in overrides"
            :key="`o-${o.client_id}`"
            class="sao__override"
        >
            <span
                class="sao__chip sao__chip--override"
                :data-testid="`dla-card-overridden-${card.id}-${o.client_id}`"
            >{{ overrideLabel(o) }}</span>
            <button
                v-if="canOverride && o.can_retract && retractClientId !== o.client_id"
                type="button"
                class="sao__btn sao__btn--link"
                :data-testid="`dla-card-override-retract-open-${card.id}-${o.client_id}`"
                :disabled="submitting"
                @click="openRetract(o)"
            >Retract</button>
            <div
                class="sao__why"
                :data-testid="`dla-card-overridden-reason-${card.id}-${o.client_id}`"
            >{{ overrideWhy(o) }}</div>

            <div
                v-if="retractClientId === o.client_id"
                class="sao__confirm sao__confirm--full"
                role="alertdialog"
                :aria-labelledby="`sao-retract-copy-${card.id}-${o.client_id}`"
                :data-testid="`dla-card-override-retract-confirm-${card.id}-${o.client_id}`"
            >
                <p
                    :id="`sao-retract-copy-${card.id}-${o.client_id}`"
                    class="sao__confirm-copy"
                >
                    Retract the approval of this size on behalf of
                    <strong>{{ o.client_name }}</strong>? It will no longer
                    count as the client's approval, including for opening
                    stores. If it was sent to the client, it goes back to
                    waiting for them.
                </p>
                <label
                    class="sao__label"
                    :for="`sao-retract-reason-${card.id}-${o.client_id}`"
                >Reason (required, only your team sees it)</label>
                <textarea
                    :id="`sao-retract-reason-${card.id}-${o.client_id}`"
                    v-model="retractReason"
                    class="sao__textarea"
                    rows="2"
                    :maxlength="RETRACT_REASON_MAX"
                    :data-testid="`dla-card-override-retract-reason-${card.id}-${o.client_id}`"
                />
                <div class="sao__actions">
                    <button
                        type="button"
                        class="sao__action sao__action--danger"
                        :data-testid="`dla-card-override-retract-submit-${card.id}-${o.client_id}`"
                        :disabled="submitting || !retractReason.trim()"
                        @click="submitRetract(o)"
                    >Retract approval</button>
                    <button
                        type="button"
                        class="sao__action"
                        :data-testid="`dla-card-override-retract-cancel-${card.id}-${o.client_id}`"
                        :disabled="submitting"
                        @click="closeRetract"
                    >Cancel</button>
                </div>
                <p
                    v-if="retractError"
                    class="sao__error"
                    :data-testid="`dla-card-override-retract-error-${card.id}-${o.client_id}`"
                >{{ retractError }}</p>
            </div>
        </div>

        <p
            v-if="retractedNotice"
            class="sao__retracted"
            :data-testid="`dla-card-override-retracted-${card.id}`"
        >{{ retractedNotice }}</p>

        <div
            v-for="c in awaiting"
            :key="`a-${c.id}`"
            class="sao__awaiting"
            :data-testid="`dla-card-awaiting-${card.id}-${c.id}`"
        >
            <span class="sao__chip sao__chip--awaiting">
                Awaiting {{ c.name }}
            </span>
            <button
                v-if="canOverride && openClientId !== c.id"
                type="button"
                class="sao__btn sao__btn--link"
                :data-testid="`dla-card-override-open-${card.id}-${c.id}`"
                :disabled="submitting"
                @click="open(c)"
            >Approve on behalf of {{ c.name }}</button>
        </div>

        <div
            v-if="showUnsentAction && !picking"
            class="sao__awaiting"
        >
            <button
                type="button"
                class="sao__btn sao__btn--link"
                :data-testid="`dla-card-approve-unsent-open-${card.id}`"
                :disabled="submitting"
                @click="openUnsent"
            >Approve on behalf of a client</button>
        </div>

        <div
            v-if="picking"
            class="sao__confirm"
            :data-testid="`dla-card-approve-unsent-confirm-${card.id}`"
        >
            <p class="sao__confirm-copy">
                Approve this size on behalf of a client? It has not been sent
                to them. It counts as the client's approval, including for
                opening stores, and it is recorded as approved by you, not by
                the client.
            </p>
            <label
                class="sao__label"
                :for="`sao-client-${card.id}`"
            >Client</label>
            <select
                :id="`sao-client-${card.id}`"
                class="sao__select"
                :data-testid="`dla-card-approve-unsent-client-${card.id}`"
                :value="pickedClientId == null ? '' : pickedClientId"
                @change="onClientPicked($event.target.value)"
            >
                <option
                    value=""
                    disabled
                >Select a client</option>
                <option
                    v-for="c in candidates"
                    :key="c.id"
                    :value="c.id"
                >{{ c.name }}</option>
            </select>
            <div
                v-if="reasonsLoading"
                class="sao__loading"
            >Loading reasons...</div>
            <ReasonPicker
                v-else
                v-model="reasonId"
                v-model:notes="notes"
                :reasons="reasons"
                :notes-required="true"
                :confirm-blocked="pickedClientId == null"
                notes-help="Internal: only your team sees this note. Your client sees the reason you pick."
                confirm-label="Approve on behalf of client"
                @confirm="submit"
                @cancel="close"
            />
            <p
                v-if="error"
                class="sao__error"
                :data-testid="`dla-card-override-error-${card.id}`"
            >{{ error }}</p>
        </div>

        <div
            v-if="openClient"
            class="sao__confirm"
            :data-testid="`dla-card-override-confirm-${card.id}`"
        >
            <p class="sao__confirm-copy">
                Approve this size on behalf of
                <strong>{{ openClient.name }}</strong>? It counts as the
                client's approval, including for opening stores, and it is
                recorded as approved by you, not by the client.
            </p>
            <div
                v-if="reasonsLoading"
                class="sao__loading"
            >Loading reasons...</div>
            <ReasonPicker
                v-else
                v-model="reasonId"
                v-model:notes="notes"
                :reasons="reasons"
                :notes-required="true"
                notes-help="Internal: only your team sees this note. Your client sees the reason you pick."
                confirm-label="Approve on behalf of client"
                @confirm="submit"
                @cancel="close"
            />
            <p
                v-if="error"
                class="sao__error"
                :data-testid="`dla-card-override-error-${card.id}`"
            >{{ error }}</p>
        </div>
    </div>
</template>

<script>
import axios from '@/axios'
import ReasonPicker from '@/components/ReasonPicker.vue'

// The endpoint's limit (approval_service.ON_BEHALF_RETRACT_REASON_MAX_LEN).
const RETRACT_REASON_MAX = 500

export default {
    name: 'SizeApprovalOverride',
    components: { ReasonPicker },
    props: {
        /** One decoration card from GET /api/artworks/<id>/decorations. */
        card: {
            type: Object,
            required: true,
        },
        /** Caller holds distributor.decoration_library.override. */
        canOverride: {
            type: Boolean,
            default: false,
        },
        /**
         * The clients a size may be approved for on their behalf:
         * ``[{id, name}]``, already narrowed by the page to the artwork's
         * distributor's active clients.
         */
        clients: {
            type: Array,
            default: () => [],
        },
        /** The artwork's own clients (header ``client_ids``), to preselect. */
        artworkClientIds: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['overridden', 'retracted'],
    data() {
        return {
            RETRACT_REASON_MAX,
            // The override whose Retract panel is open (its client id).
            retractClientId: null,
            retractReason: '',
            retractError: null,
            // What the last retract on this card did, shown until the next action.
            retractedNotice: null,
            openClientId: null,
            // The never-sent panel: open, and the client picked in it.
            picking: false,
            pickedClientId: null,
            reasons: [],
            reasonsLoading: false,
            reasonId: null,
            notes: '',
            submitting: false,
            error: null,
        }
    },
    computed: {
        awaiting() {
            return Array.isArray(this.card?.awaiting_clients)
                ? this.card.awaiting_clients
                : []
        },
        overrides() {
            return Array.isArray(this.card?.approval_overrides)
                ? this.card.approval_overrides
                : []
        },
        openClient() {
            if (this.openClientId == null) return null
            return this.awaiting.find((c) => c.id === this.openClientId) || null
        },
        candidates() {
            // Clients this size has nothing for yet. An approved client
            // needs nothing; an awaiting one has its own action above.
            const taken = new Set([
                ...(this.card?.approved_clients || []).map((c) => Number(c.id)),
                ...this.awaiting.map((c) => Number(c.id)),
            ])
            return (Array.isArray(this.clients) ? this.clients : []).filter(
                (c) => !taken.has(Number(c.id)),
            )
        },
        showUnsentAction() {
            return this.canOverride && this.candidates.length > 0
        },
    },
    methods: {
        overrideLabel(o) {
            const base = `Approved on behalf of ${o.client_name}`
            return o.by_name ? `${base} by ${o.by_name}` : base
        },
        overrideWhy(o) {
            const parts = []
            if (o.reason) parts.push(o.reason)
            if (o.notes) parts.push(o.notes)
            const when = this.formatDate(o.at)
            if (when) parts.push(when)
            return parts.join(' -- ')
        },
        formatDate(iso) {
            if (!iso) return ''
            const d = new Date(iso)
            if (Number.isNaN(d.getTime())) return ''
            return d.toLocaleDateString()
        },
        async open(client) {
            this.closeRetract()
            this.retractedNotice = null
            this.picking = false
            this.openClientId = client.id
            this.reasonId = null
            this.notes = ''
            this.error = null
            await this.loadReasons()
        },
        async openUnsent() {
            this.closeRetract()
            this.retractedNotice = null
            this.openClientId = null
            this.picking = true
            // Preselect the artwork's own client only when that is not a
            // guess: exactly one of its clients is offered.
            const own = new Set(this.artworkClientIds.map(Number))
            const mine = this.candidates.filter((c) => own.has(Number(c.id)))
            this.pickedClientId = mine.length === 1 ? Number(mine[0].id) : null
            this.reasonId = null
            this.notes = ''
            this.error = null
            await this.loadReasons()
        },
        openRetract(override) {
            this.close()
            this.retractedNotice = null
            this.retractClientId = override.client_id
            this.retractReason = ''
            this.retractError = null
        },
        closeRetract() {
            this.retractClientId = null
            this.retractReason = ''
            this.retractError = null
        },
        async submitRetract(override) {
            const reason = String(this.retractReason || '').trim()
            if (!reason || this.submitting) return
            this.submitting = true
            this.retractError = null
            try {
                const resp = await axios.post(
                    `/api/decorations/${this.card.id}/approve-on-behalf/retract`,
                    { client_id: override.client_id, reason }
                )
                this.closeRetract()
                this.retractedNotice =
                    `Retracted. ${override.client_name} has not approved this ` +
                    'size; stores using it need their approval before they can open.'
                this.$emit('retracted', resp?.data || {})
            } catch (err) {
                const data = err?.response?.data || {}
                this.retractError =
                    data.error ||
                    'Could not retract this approval. Please try again.'
            } finally {
                this.submitting = false
            }
        },
        onClientPicked(raw) {
            this.pickedClientId = raw === '' || raw == null ? null : Number(raw)
        },
        close() {
            this.openClientId = null
            this.picking = false
            this.pickedClientId = null
            this.error = null
        },
        async loadReasons() {
            if (this.reasons.length) return
            this.reasonsLoading = true
            try {
                const resp = await axios.get('/api/bypass_reasons/')
                const rows = resp?.data?.data
                this.reasons = Array.isArray(rows)
                    ? rows
                        .map((row) => ({
                            ...(row.attributes || {}),
                            id: Number(row.id),
                        }))
                        .filter((r) => r.is_active !== false)
                    : []
                if (!this.reasons.length) {
                    this.error = 'No approval reasons are available.'
                }
            } catch (err) {
                this.error = 'Could not load approval reasons.'
            } finally {
                this.reasonsLoading = false
            }
        },
        async submit() {
            const notes = String(this.notes || '').trim()
            if (this.reasonId == null || !notes || this.submitting) return
            const reasonId = Number(this.reasonId)
            let clientId
            let request
            if (this.picking) {
                if (this.pickedClientId == null) return
                clientId = this.pickedClientId
                request = () =>
                    axios.post(
                        `/api/decorations/${this.card.id}/approve-on-behalf`,
                        { client_id: clientId, reason_id: reasonId, notes }
                    )
            } else {
                const client = this.openClient
                if (!client) return
                clientId = client.id
                request = () =>
                    axios.post(
                        `/api/approval_records/${client.approval_record_id}/bypass`,
                        { reason_id: reasonId, notes }
                    )
            }
            this.submitting = true
            this.error = null
            try {
                await request()
                this.openClientId = null
                this.picking = false
                this.pickedClientId = null
                this.$emit('overridden', {
                    decorationId: this.card.id,
                    clientId,
                })
            } catch (err) {
                const data = err?.response?.data || {}
                this.error =
                    data.error ||
                    'Could not approve this size. Please try again.'
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.sao {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 10px;
}
.sao__awaiting,
.sao__override {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.sao__chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: var(--tf-radius-pill);
    font-size: 11.5px;
    font-weight: 500;
}
.sao__chip--awaiting {
    background: var(--tf-chip-pending-bg);
    color: var(--tf-chip-pending-fg);
}
.sao__chip--override {
    background: var(--tf-chip-success-bg);
    color: var(--tf-success-dark);
    border: 1px dashed var(--tf-chip-success-border);
}
.sao__why {
    flex-basis: 100%;
    font-size: 12px;
    color: var(--tf-fg-4);
}
.sao__btn {
    appearance: none;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: 12.5px;
    cursor: pointer;
}
.sao__btn--link {
    color: var(--tf-primary);
    text-decoration: underline;
}
.sao__btn:disabled {
    cursor: default;
    opacity: 0.6;
}
.sao__confirm {
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    background: var(--tf-surface-alt);
    padding: 10px 12px;
}
.sao__confirm-copy {
    margin: 0 0 6px;
    font-size: 13px;
    color: var(--tf-fg-2);
}
.sao__label {
    display: block;
    margin: 0 0 4px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--tf-fg-2);
}
.sao__select {
    display: block;
    width: 100%;
    max-width: 320px;
    margin: 0 0 8px;
    padding: 4px 8px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    background: var(--tf-surface);
    font: inherit;
    font-size: 13px;
}
.sao__loading {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.sao__confirm--full {
    flex-basis: 100%;
}
.sao__textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    margin: 0 0 8px;
    padding: 6px 8px;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    background: var(--tf-surface);
    font: inherit;
    font-size: 13px;
    resize: vertical;
}
.sao__actions {
    display: flex;
    gap: 8px;
}
.sao__action {
    font: inherit;
    font-size: 12.5px;
    border: 1px solid var(--tf-border);
    background: var(--tf-surface);
    border-radius: var(--tf-radius-md);
    padding: 4px 10px;
    cursor: pointer;
}
.sao__action:disabled {
    opacity: 0.6;
    cursor: default;
}
.sao__action--danger {
    background: #b42318;
    border-color: #b42318;
    color: #ffffff;
}
.sao__retracted {
    margin: 0;
    font-size: 12.5px;
    color: #a15c00;
}
.sao__error {
    margin: 6px 0 0;
    font-size: 12.5px;
    color: var(--tf-chip-error-fg);
}
</style>
